import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AumicoAbcpConfig, AumicoAbcpConfigService } from '../abcp-data.config';
import { IAccountingFilePreview, IClosingSetup, IStartClosingRequest } from '../models/closing-setup.model';

/**
 * HTTP for the Setup-Wizard (REV-21354, `ClosingSetupController` in `aumico-be`).
 *
 * TWO calls, and deliberately no third for polling: the launch status IS the setup, so following a
 * running pipeline is the same `read()` the navigation gate uses. A separate status route would let
 * the launch screen and the navigation disagree about whether a closing has started.
 */
@Injectable()
export class ClosingSetupService {
    private readonly baseUrl: string;

    public constructor(
        private readonly http: HttpClient,
        @Inject(AumicoAbcpConfigService) config: AumicoAbcpConfig,
    ) {
        // NO leading slash: `apiGateway` already ends with one, so `${config.apiGateway}/projects`
        // yields `/api//projects` and the gateway 404s. Same concatenation as `closing-document.service.ts`.
        this.baseUrl = `${config.apiGateway}projects`;
    }

    /**
     * The closing's setup, or `null` when the wizard has never run.
     *
     * `null` is the ordinary answer on the way in, not an error — the server returns it with a 200
     * for exactly that reason.
     */
    public read(projectId: string): Observable<IClosingSetup | null> {
        return this.http.get<IClosingSetup | null>(`${this.baseUrl}/${projectId}/abcp/setup`);
    }

    /**
     * Records the answers and starts the launch pipeline.
     *
     * Resolves as soon as the record exists with its steps `PENDING`; the pipeline runs on server-side
     * and the caller follows it with `read()`.
     */
    public start(projectId: string, request: IStartClosingRequest): Observable<IClosingSetup> {
        return this.http.post<IClosingSetup>(`${this.baseUrl}/${projectId}/abcp/setup/start`, request);
    }

    /**
     * REV-22007 — uploads a trial balance (`POST …/abcp/accounting-file`, multipart field `file`).
     *
     * The server parses it on the request: a usable file is stored and answered with its preview, a
     * refused one answers 422 with `{ messageKey, rows }` and nothing is stored. The import runs later
     * — at the start, or from the workspace — because it may only read the file once it is scanned.
     */
    public uploadAccountingFile(projectId: string, file: File): Observable<IAccountingFilePreview> {
        const body = new FormData();
        body.append('file', file, file.name);

        return this.http.post<IAccountingFilePreview>(`${this.baseUrl}/${projectId}/abcp/accounting-file`, body);
    }
}
